import React, {useEffect, useMemo, useState} from 'react';
import Page from "../../Components/Page";
import {Breadcrumbs} from "../../Components/Breadcrumbs";
import {useServiceBase} from "../../Components/ReactTable/useServiceBase";
import {LoadingWrapper} from "../../Components/LoadingWrapper";
import EditableReactTableV2 from "../../Components/ReactTable/EditableReactTableV2";
import {INDEX_FIELD} from "../../Components/ReactTable/common_custom_columns";
import {
    EditFormBooleanDropdownInputField,
    EditFormDropdownInputField,
    EditFormNumberInputField,
    EditFormTextInputField
} from "../../Components/ReactTable/EditRecordForm";
import {makeApiCall, makeApiCallPost, makeApiCallWithoutCatch} from "../../../Api/makeApiCall";
import {toastError, toastSuccess} from "../../../utils/toast";
import {toast} from "react-toastify";
import {MarketParentageDropdownField} from "./MarketParentageDropdownField";
import {faIcons} from "../../../utils/iconConstants";
import ConfirmationModal from "../../Components/ConfirmationModal";
import SelectDropdown from "../../Components/Dropdowns/SelectDropdown";
import _ from "lodash";
import {
    buildChildIdMapping,
    buildMergedParentageMappings,
    buildParentIdMapping,
    checkChildren,
    checkChildrenDuplicates,
    checkParentDuplicates,
    checkParents
} from "./marketParentageUtils";


const MarketServiceTwo = () => {
    const endpointUrl = `${process.env.REACT_APP_EDP_API_BASE_URL}/marketsTwo/`;
    const { loading, data, onRowEditConfirmed, onRowDeleteConfirmed } = useServiceBase({ endpointUrl });
    const [marketParentage, setMarketParentage] = useState([]);
    const [showMarketsModal, setShowMarketsModal] = useState(false);
    const [showLoadingModal, setShowLoadingModal] = useState(false);

    const [parentIdMapping, setParentIdMapping] = useState({});
    const [currentParentForMarket, setCurrentParentForMarket] = useState({});
    const [parentErrors, setParentErrors] = useState(undefined);

    const [childIdMapping, setChildIdMapping] = useState({});
    const [currentChildForMarket, setCurrentChildForMarket] = useState({});
    const [childErrors, setChildErrors] = useState(undefined);

    const [currentMarketsForParentage, setCurrentMarketsForParentage] = useState({});


    useEffect(() => {
        makeApiCallWithoutCatch(`${process.env.REACT_APP_EDP_API_BASE_URL}/marketsTwoParentage`)
            .then((marketsParentage) => {
                setMarketParentage(marketsParentage.data);
                setParentIdMapping(buildParentIdMapping(marketsParentage.data));
                setChildIdMapping(buildChildIdMapping(marketsParentage.data));
            });
    }, []);

    const markets = useMemo(() => {
        return data?.data?.map((market) => ({
            value: market.marketId,
            label: `${market.marketId} - ${market.description}`,
            marketTag: market.marketTag
        })) || [];
    }, [data]);

    const marketsWithTags = useMemo(() => {
        return data?.data?.map((market) => ({
            value: market.marketId,
            label: `${market.marketTag} - ${market.description}`,
            marketTag: market.marketTag
        })) || [];
    }, [data]);

    const customColumns = [
        {
            header: "Market Id", sticky: "left", size: 120, accessorKey: "marketId",
            cell: ({row}) => {
                const href = `/marketsToStores/${row.original.marketId}`;
                return <a href={href} target="_blank" rel="noopener noreferrer">{row.original.marketId}</a>
            }
        },
        {header:"Market Tag",sticky: "left", size: 120, accessorKey: "marketTag"},
        {header:"Description", sticky: "left", size: 460, accessorKey: "description"},
        {header:"Level", accessorKey: "level"},
        {header:"Universe", accessorKey: "universe"},
        {header:"Order Number", accessorKey: "orderNumber"},
        {header:"PVL Flag", accessorKey: "pvlFlag"},
        {header:"SNE Flag", accessorKey: "sneFlag"},
        {header:"Mkt Lvl Ind", accessorKey: "mktLvlInd"},
        {header:"Store Level", accessorKey: "isStoreLevel"},
        {header:"Control Brand Build", size: 180, accessorKey: "controlBrandBuild"},
        {header:"SVC Name", size: 200,accessorKey: "svcName"},
        {header:"SCV Growth Factor", size: 180, accessorKey: "scvGrowthFactor"},
        {header:"Store Count Growth Factor", size: 250, accessorKey: "storeCntGrowthFactorOverride"}
    ]

    const levelOptions = [
        { value: "CM", label: "CM" },
        { value: "PM", label: "PM" },
        { value: "PT", label: "PT" }
    ];
    const sneOptions = [
        { value: "N", label: "N" },
        { value: "R", label: "R" }
    ];
    const pvlFlagOptions = [
        { value: "S", label: "S" },
        { value: "R", label: "R" }
    ];
    const universeOptions = [
        { value: "SG", label: "SG" },
        { value: "SN", label: "SN" },
        { value: "SA", label: "SA" }
    ];
    const mktLvlIndOptions = [
        { value: "A", label: "A" },
        { value: "R", label: "R" },
        { value: "T", label: "T" }
    ];
    const dataMartOptions = [
        {value: 1, label: "SN"},
        {value: 2, label: "SG"},
        {value: 3, label: "SA"}
    ]

    const onRowDeleteConfirmedWrapper = async (row) => {
        if (await checkMarketToStores(row.marketId)) {
            toastError(toast, 'This market is linked to stores. Please delete the stores first.');
            return;
        }
        if (marketParentage.some(parentage => parentage.parentId === row.marketId)) {
            toastError(toast, 'This market is a parent of another market. Please delete the child relationship first.');
            return;
        }

        await makeApiCallPost(
            `${process.env.REACT_APP_EDP_API_BASE_URL}/marketsTwoParentage/marketsTwoParentageUpsert`,
            {updateMarketIds: [], marketId: row.marketId, updateType: 'parent'}
        );
        await onRowDeleteConfirmed(row);
    }

    async function checkMarketToStores(marketId) {
        const results = await makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/marketsToStores/?market_id=${marketId}`)
        return results?.data.length > 0;
    }
    
    function onRowEditConfirmedWrapper(newOrUpdatedRow) {
        const {parentId, parentMarketTagsForMaryAndAmy, childId, childMarketTagsForMaryAndAmy, ...sanitizedData} = newOrUpdatedRow;
        return onRowEditConfirmed(sanitizedData);
    }
    
    function getParentageForIndividualMarket(row) {
        setCurrentParentForMarket(marketParentage.filter(option => option.childId === row.marketId))
        setCurrentMarketsForParentage(row)
        setCurrentChildForMarket(marketParentage.filter(option => option.parentId === row.marketId))
        setShowMarketsModal(true);
    }

    function marketsModalCancellationCallBack() {
        setCurrentParentForMarket([])
        setCurrentChildForMarket([])
        setParentErrors(undefined);
        setChildErrors(undefined);

        setCurrentMarketsForParentage({})
        setShowMarketsModal(false);
    }

    function makeCallToUpsertMarketParentage(updateParentMarketIds, updateChildMarketIds, marketId) {
        makeApiCallPost(
            `${process.env.REACT_APP_EDP_API_BASE_URL}/marketsTwoParentage/marketsTwoParentageUpsert`,
            {updateParentMarketIds, updateChildMarketIds, marketId}
        ).then((response) => {
            if (response?.apiError) {
                toastError(toast, `Failed to update marketId ${marketId}`);
            } else {
                makeApiCallWithoutCatch(`${process.env.REACT_APP_EDP_API_BASE_URL}/marketsTwoParentage`)
                    .then((marketsParentage) => {
                        toastSuccess(toast, `Successfully added Parent: ${updateParentMarketIds} and Child: ${updateChildMarketIds} to marketId ${marketId}`);
                        setMarketParentage(marketsParentage.data);
                        setParentIdMapping(buildParentIdMapping(marketsParentage.data));
                        setChildIdMapping(buildChildIdMapping(marketsParentage.data));
                    }).finally(() => {
                        setShowLoadingModal(false);
                        marketsModalCancellationCallBack()
                    })
            }
        });

    }

    const setParentErrorMessage = (message) => {
        setParentErrors(message);
        setShowLoadingModal(false);
    }

    const setChildErrorMessage = (message) => {
        setChildErrors(message);
        setShowLoadingModal(false);
    }

    const validateAndSaveMarketParentage = () => {
        const updateParentMarketIds = _.map(currentParentForMarket, 'parentId');
        const updateChildMarketIds = _.map(currentChildForMarket, 'childId');
        const marketId = currentMarketsForParentage.marketId;
        const marketIdDescription = `${marketId} - ${currentMarketsForParentage.description}`;

        const {mergedParentIdMapping, mergedChildIdMapping} = buildMergedParentageMappings(
            parentIdMapping, childIdMapping, marketId, updateParentMarketIds, updateChildMarketIds
        );

        let invalidParents = checkParents(marketId, mergedParentIdMapping, [marketId]);

        if (invalidParents.length === 1 && invalidParents.includes(marketId)) {
            setParentErrorMessage(`Market Id ${marketIdDescription} cannot be assigned to its own parent, nor can it be the parent of its own parent.`);
            return
        }

        if (invalidParents.length > 0) {
            setParentErrorMessage(`This markets id/s [${invalidParents.join(', ')}] are invalid parents for market id ${marketIdDescription}. Due to circular parentage`);
            return
        }

        invalidParents = checkParentDuplicates(marketId, mergedParentIdMapping)
        if (invalidParents.length > 0) {
            setParentErrorMessage(`This markets id [${invalidParents.join(', ')}] is invalid parent for market id ${marketIdDescription}. Due to duplicate parentage`);
            return
        }

        let invalidChildren = checkChildren(marketId, mergedChildIdMapping, [marketId]);
        if (invalidChildren.length === 1 && invalidChildren.includes(marketId)) {
            setChildErrorMessage(`Market Id ${marketIdDescription} cannot be assigned to its own child, nor can it be the child of its own child.`)
            return
        }
        if (invalidChildren.length > 0) {
            setChildErrorMessage(`This markets id/s [${invalidChildren.join(', ')}] are invalid children for market id ${marketIdDescription}. Due to circular parentage`);
            return
        }

        invalidChildren = checkChildrenDuplicates(marketId, mergedChildIdMapping, [marketId]);
        if (invalidChildren.length > 0) {
            setChildErrorMessage(`This markets id [${invalidChildren}] is invalid child for market id ${marketIdDescription}. Due to duplicate parentage`);
            return
        }
        const overlappingIds = updateParentMarketIds.filter(parentId => updateChildMarketIds.includes(parentId));
        if (overlappingIds.length > 0) {
            setParentErrorMessage(`Market Id/s [${overlappingIds.join(', ')}] cannot be both parent and child of market id ${marketIdDescription}.`);
            setChildErrorMessage(`Market Id/s [${overlappingIds.join(', ')}] cannot be both parent and child of market id ${marketIdDescription}.`);
            return;
        }


        if (invalidParents.length === 0 && invalidChildren.length === 0 ){
            setParentErrors(undefined);
            setChildErrors(undefined);
            makeCallToUpsertMarketParentage(updateParentMarketIds, updateChildMarketIds, marketId);
        }
    }

    return <Page breadCrumbs={<Breadcrumbs items={[{label: "Services"}, {label: "Market"}, {label: "Ops Market Service"}]}/>} name={"Ops Market Service"} beta={true}>
        <LoadingWrapper loading={loading}>
            <EditableReactTableV2
                filterable
                data={data.data}
                columns={customColumns}
                initialState={{ pagination: { pageSize: 10 }, sorting: [{ id: "marketId", desc: false }] }}
                actionButtonsProps={{sticky: true}}
                customActionComponent={(row) =>
                    <button>
                        <i data-testid={"react-table-add-market-record-button"}
                           title={'Add Market Parentage'}
                           className={faIcons.market}
                           onClick={() => getParentageForIndividualMarket(row)}
                        />
                    </button>}
                editTable={[
                    INDEX_FIELD,
                    {id: "marketId", Component: args => <EditFormNumberInputField  {...args} disabled />},
                    {id: "description", Component: args => <EditFormTextInputField  {...args} isRequired />},
                    {id: "marketTag", Component: args => <EditFormNumberInputField  {...args} disabled />},
                    {id: "level", Component: args => <EditFormDropdownInputField  {...args} isRequired  options={levelOptions}/>},
                    {id: "sneFlag", defaultValue: "N", Component: args => <EditFormDropdownInputField  {...args} isRequired  options={sneOptions}/>},
                    {id: "orderNumber", Component: args => <EditFormNumberInputField  {...args} />},
                    {id: "pvlFlag", Component: args => <EditFormDropdownInputField  {...args} isClearable options={pvlFlagOptions}/>},
                    {id: "universe", Component: args => <EditFormDropdownInputField  {...args} isClearable options={universeOptions}/>},
                    {id: "isStoreLevel", defaultValue:false, Component: args => <EditFormBooleanDropdownInputField  {...args} label={"Is Store Level"} />},
                    {id: "mktLvlInd", Component: args => <EditFormDropdownInputField  {...args} isClearable options={mktLvlIndOptions}/>},
                    {id: "controlBrandBuild", defaultValue:false, Component: args => <EditFormBooleanDropdownInputField  {...args} label={"Is Control Brand Build"} />},
                    {id: "svcName", Component: args => <EditFormTextInputField  {...args} />},
                    {id: "scvGrowthFactor", Component: args => <EditFormNumberInputField  {...args} />},
                    {id: "storeCntGrowthFactorOverride", Component: args => <EditFormNumberInputField  {...args} label={"Store Count Growth Factor"} />},
                    {id: "datamartId", Component: args => <EditFormDropdownInputField  {...args} isClearable options={dataMartOptions} label={"Datamart"} />},
                    {id: "parentId", defaultValue: [], Component: args => <MarketParentageDropdownField  {...args} label={"Parent Markets by Ids"} marketParentage={marketParentage} markets={markets} />},
                    {id: "parentMarketTagsForMaryAndAmy", defaultValue: [], Component: args => <MarketParentageDropdownField  {...args} label={"Parent Markets by Market Tag"} marketParentage={marketParentage} markets={marketsWithTags}/>},
                    {id: "childId", defaultValue: [], Component: args => <MarketParentageDropdownField  {...args} label={"Child Markets by Ids"} marketParentage={marketParentage} markets={markets} />},
                    {id: "childMarketTagsForMaryAndAmy", defaultValue: [], Component: args => <MarketParentageDropdownField  {...args} label={"Child Markets by Market Tag"} marketParentage={marketParentage} markets={marketsWithTags}/>},
                ]}
                editModalAdditionalClass={"market-service-two-edit-modal"}
                onRowEditConfirmed={onRowEditConfirmedWrapper}
                onRowDeleteConfirmed={onRowDeleteConfirmedWrapper}
            >
                {
                    () => {
                        return (
                            <div>
                                {showMarketsModal && <ConfirmationModal
                                    modalClassName={"Modal market-parentage-modal"}
                                    confirmButtonText={"Save"}
                                    headerText={`Edit Parentage Markets for ${currentMarketsForParentage.description} - ${currentMarketsForParentage.marketId}`}
                                    cancellationCallback={() => marketsModalCancellationCallBack()}
                                    confirmationCallback={() => validateAndSaveMarketParentage()}
                                    loading={showLoadingModal}
                                    >
                                    <div data-testid={'market-parentage-modal-content'}
                                         className={'market-parentage-modal-content'} >
                                        <label htmlFor="parent_markets">Edit Parent Markets</label>
                                        <SelectDropdown
                                            id={`parent_markets_dropdown`}
                                            inputId={'parent_markets'}
                                            dataTestId={"parent_markets_dropdown"}
                                            options={markets}
                                            onChange={(options) => {
                                                const mappedOptions = options && options.length > 0 ? options.map(x => ({parentId: x?.value })) : []
                                                setCurrentParentForMarket(mappedOptions);
                                                setParentErrors(undefined);
                                            }}
                                            selectValues={_.map(currentParentForMarket, 'parentId')}
                                            isMulti={true}
                                            isClearable={true}
                                        />
                                        <label data-testid={"parent_errors"} className={'parent-errors'}>{parentErrors}</label>
                                        <br />
                                        <label htmlFor="markets">Edit Child Markets</label>
                                        <SelectDropdown
                                            id={`child_markets_dropdown`}
                                            inputId={'markets'}
                                            dataTestId={"child_markets_dropdown"}
                                            options={markets}
                                            onChange={(options) => {
                                                const mappedOptions = options && options.length > 0 ? options.map(x => ({childId: x?.value })) : []
                                                setCurrentChildForMarket(mappedOptions);
                                                setChildErrors(undefined);
                                            }}
                                            selectValues={_.map(currentChildForMarket, 'childId')}
                                            isMulti={true}
                                            isClearable={true}
                                        />
                                        <label data-testid={"child_errors"} className={'child-errors'}>{childErrors}</label>
                                    </div>
                                </ConfirmationModal>
                                }
                            </div>
                        )
                    }
                }
            </EditableReactTableV2>
        </LoadingWrapper>
    </Page>;
};

export default MarketServiceTwo;